import { useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, LoaderCircle, Pause, Play, RotateCcw } from 'lucide-react';

import {
  SynchronizedYoutubePlayer,
  type SynchronizedYoutubePlayerHandle,
} from '@/components/SynchronizedYoutubePlayer';
import { Button } from '@/components/ui/button';
import type { YoutubeCaption, YoutubeHighlightSpan } from '@/services';

/** The Replay arrow with the jump length inside it, so the seek buttons read as a set with Replay. */
export function SeekIcon({ direction }: { direction: 'back' | 'forward' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="size-5"
    >
      {direction === 'back' ? (
        <>
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </>
      ) : (
        <>
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1.03 6.74 2.74L21 8" />
          <path d="M21 3v5h-5" />
        </>
      )}
      <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="700" fill="currentColor" stroke="none">
        5
      </text>
    </svg>
  );
}

interface VideoPlayerWithControlsProps {
  videoId: string;
  playbackStartMilliseconds: number;
  sentenceId: number | undefined;
  highlightSpans: YoutubeHighlightSpan[];
  captions: YoutubeCaption[];
  captionsLoading: boolean;
  captionsError: string;
  activeIndex: number;
  canMoveNext: boolean;
  loadingMore: boolean;
  loadMoreError: string;
  onPrevious: () => void;
  onNext: () => void;
  overlay?: ReactNode;
  frameClassName?: string;
  captionClassName?: string;
}

/** Player + subtitle box + transport controls (Previous / Replay / seek / play-pause / Next), shared
 * between a card's Videos tab and the standalone /videos search so the two always look and behave
 * the same way — one player instance and one control row, not two independently styled copies. */
export function VideoPlayerWithControls({
  videoId,
  playbackStartMilliseconds,
  sentenceId,
  highlightSpans,
  captions,
  captionsLoading,
  captionsError,
  activeIndex,
  canMoveNext,
  loadingMore,
  loadMoreError,
  onPrevious,
  onNext,
  overlay,
  frameClassName,
  captionClassName,
}: VideoPlayerWithControlsProps) {
  const playerHandleRef = useRef<SynchronizedYoutubePlayerHandle>(null);
  const [playerStatus, setPlayerStatus] = useState({ loading: true, error: false });
  const [playing, setPlaying] = useState(false);
  const playbackDisabled = playerStatus.loading || playerStatus.error;

  return (
    <div className="flex flex-col gap-4">
      <SynchronizedYoutubePlayer
        key={videoId}
        ref={playerHandleRef}
        videoId={videoId}
        playbackStartMilliseconds={playbackStartMilliseconds}
        sentenceId={sentenceId}
        highlightSpans={highlightSpans}
        captions={captions}
        captionsLoading={captionsLoading}
        captionsError={captionsError}
        onStatusChange={setPlayerStatus}
        onPlayingChange={setPlaying}
        overlay={overlay}
        frameClassName={frameClassName}
        captionClassName={captionClassName}
      />

      {loadMoreError && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadMoreError}
        </p>
      )}

      {/* flex-wrap only engages once the row's minimum content (two labelled buttons + four icon
          buttons) genuinely can't fit — sub-360px phone widths — so it stays one row everywhere
          else; without it, narrow viewports clip Previous/Next past the screen edge with no way
          to reach them. */}
      <div className="flex flex-wrap items-center justify-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
          disabled={activeIndex === 0}
          className="h-10 rounded-full border-slate-200 bg-white px-4"
        >
          <ChevronLeft className="size-4" />
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => playerHandleRef.current?.replay()}
          disabled={playbackDisabled}
          className="h-10 rounded-full border-slate-200 bg-white px-4 text-slate-700 hover:bg-slate-50"
        >
          <RotateCcw className="size-4" />
          Replay
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Back 5 seconds"
          onClick={() => playerHandleRef.current?.seekBy(-5)}
          disabled={playbackDisabled}
          className="size-10 rounded-full border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          <SeekIcon direction="back" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={playing ? 'Pause' : 'Play'}
          onClick={() => playerHandleRef.current?.togglePlay()}
          disabled={playbackDisabled}
          className="size-10 rounded-full border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
        >
          {playing ? (
            <Pause className="size-4 fill-current" />
          ) : (
            <Play className="size-4 fill-current" />
          )}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Forward 5 seconds"
          onClick={() => playerHandleRef.current?.seekBy(5)}
          disabled={playbackDisabled}
          className="size-10 rounded-full border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        >
          <SeekIcon direction="forward" />
        </Button>
        <Button
          type="button"
          onClick={onNext}
          disabled={!canMoveNext || loadingMore}
          className="h-10 min-w-24 shrink-0 rounded-full bg-[#496653] px-4 text-white hover:bg-[#385140]"
        >
          {loadingMore ? <LoaderCircle className="size-4 animate-spin" /> : null}
          {loadingMore ? 'Loading...' : 'Next'}
          {!loadingMore && <ChevronRight className="size-4" />}
        </Button>
      </div>
    </div>
  );
}
